import React, { useEffect } from 'react';
import { Container, Box, Typography, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

export const TermsOfServicePage: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const buildSection = (title: string, content: React.ReactNode) => (
    <Box sx={{ mb: 4 }}>
      <Typography 
        variant="h5" 
        fontWeight="bold" 
        color="#673AB7" 
        gutterBottom 
        sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' } }}
      >
        {title}
      </Typography>
      <Typography 
        variant="body1" 
        component="div"
        sx={{ lineHeight: 1.8, color: '#424242' }}
      >
        {content}
      </Typography>
    </Box>
  );

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography 
          variant="h3" 
          fontWeight="bold" 
          gutterBottom 
          sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
        >
          {t('terms_title')}
        </Typography>
      <Typography 
        variant="h5" 
        gutterBottom 
        sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }, mb: 2 }}
      >
        {t('nagrik_sahayak')} ({t('nagrik_sahayak_hindi')})
      </Typography>
      <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mb: 4 }}>
        {t('terms_last_updated')}
      </Typography>

      {buildSection(t('terms_agreement_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('terms_intro_title')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('terms_intro_website')}</Typography>
            <Typography variant="body1">• {t('terms_intro_mobile')}</Typography>
          </Box>
          <Typography variant="body1" paragraph>
            {t('terms_intro_agreement')}
          </Typography>
          <Typography variant="body1">
            {t('terms_intro_disagree')}
          </Typography>
        </>
      )}

      {buildSection(t('terms_service_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('terms_service_intro')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('terms_service_forms')}</Typography>
            <Typography variant="body1">• {t('terms_service_checklists')}</Typography>
            <Typography variant="body1">• {t('terms_service_guidance')}</Typography>
            <Typography variant="body1">• {t('terms_service_schemes')}</Typography>
            <Typography variant="body1">• {t('terms_service_links')}</Typography>
          </Box>
          <Typography variant="body1">
            {t('terms_service_purpose')}
          </Typography>
        </>
      )}

      {buildSection(t('terms_info_title'),
        <>
          <Typography variant="body1" fontWeight="bold" paragraph>
            {t('terms_info_important')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('terms_info_platform_name')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('terms_info_no_submit')}</Typography>
            <Typography variant="body1">• {t('terms_info_no_process')}</Typography>
            <Typography variant="body1">• {t('terms_info_no_advice')}</Typography>
            <Typography variant="body1">• {t('terms_info_no_guarantee')}</Typography>
            <Typography variant="body1">• {t('terms_info_no_verify')}</Typography>
          </Box>
          <Typography variant="body1" paragraph>
            {t('terms_info_sources')}
          </Typography>
          <Typography variant="body1">
            {t('terms_info_responsibility')}
          </Typography>
        </>
      )}

      {buildSection(t('terms_govt_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('terms_govt_intro')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('terms_govt_india')}</Typography>
            <Typography variant="body1">• {t('terms_govt_state')}</Typography>
            <Typography variant="body1">• {t('terms_govt_ministry')}</Typography>
            <Typography variant="body1">• {t('terms_govt_authority')}</Typography>
          </Box>
          <Typography variant="body1">
            {t('terms_govt_property')}
          </Typography>
        </>
      )}

      {buildSection(t('terms_user_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('terms_user_intro')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('terms_user_lawful')}</Typography>
            <Typography variant="body1">• {t('terms_user_verify')}</Typography>
            <Typography variant="body1">• {t('terms_user_not_rely')}</Typography>
            <Typography variant="body1">• {t('terms_user_no_misuse')}</Typography>
            <Typography variant="body1">• {t('terms_user_no_scrape')}</Typography>
            <Typography variant="body1">• {t('terms_user_comply')}</Typography>
          </Box>
          <Typography variant="body1">
            {t('terms_user_responsible')}
          </Typography>
        </>
      )}

      {buildSection(t('terms_accuracy_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('terms_accuracy_intro')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('terms_accuracy_change')}</Typography>
            <Typography variant="body1">• {t('terms_accuracy_vary')}</Typography>
            <Typography variant="body1">• {t('terms_accuracy_outdated')}</Typography>
          </Box>
          <Typography variant="body1" paragraph>
            {t('terms_accuracy_no_warrant')}
          </Typography>
          <Typography variant="body1">
            {t('terms_accuracy_verify')}
          </Typography>
        </>
      )}

      {buildSection(t('terms_third_party_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('terms_third_party_intro')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('terms_third_party_analytics')}</Typography>
            <Typography variant="body1">• {t('terms_third_party_crashlytics')}</Typography>
            <Typography variant="body1">• {t('terms_third_party_firestore')}</Typography>
            <Typography variant="body1">• {t('terms_third_party_storage')}</Typography>
          </Box>
          <Typography variant="body1" paragraph>
            {t('terms_third_party_own_terms')}
          </Typography>
          <Typography variant="body1">
            {t('terms_third_party_not_responsible')}
          </Typography>
        </>
      )}

      {buildSection(t('terms_links_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('terms_links_intro')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('terms_links_no_control')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('terms_links_external_content')}</Typography>
            <Typography variant="body1">• {t('terms_links_privacy')}</Typography>
            <Typography variant="body1">• {t('terms_links_govt_changes')}</Typography>
          </Box>
          <Typography variant="body1">
            {t('terms_links_discretion')}
          </Typography>
        </>
      )}

      {buildSection(t('terms_ip_title'),
        <>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('terms_ip_design')}</Typography>
            <Typography variant="body1">• {t('terms_ip_public')}</Typography>
            <Typography variant="body1">• {t('terms_ip_govt_property')}</Typography>
          </Box>
          <Typography variant="body1" paragraph>
            {t('terms_ip_personal_use')}
          </Typography>
          <Typography variant="body1">
            {t('terms_ip_prohibited')}
          </Typography>
        </>
      )}

      {buildSection(t('terms_liability_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('terms_liability_intro')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('terms_liability_no_indirect')}</Typography>
            <Typography variant="body1">• {t('terms_liability_no_rejection')}</Typography>
            <Typography variant="body1">• {t('terms_liability_no_decisions')}</Typography>
            <Typography variant="body1">• {t('terms_liability_no_interruptions')}</Typography>
          </Box>
          <Typography variant="body1">
            {t('terms_liability_own_risk')}
          </Typography>
        </>
      )}

      {buildSection(t('terms_availability_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('terms_availability_intro')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('terms_availability_modify')}</Typography>
            <Typography variant="body1">• {t('terms_availability_update')}</Typography>
            <Typography variant="body1">• {t('terms_availability_restrict')}</Typography>
            <Typography variant="body1">• {t('terms_availability_maintenance')}</Typography>
          </Box>
          <Typography variant="body1">
            {t('terms_availability_no_guarantee')}
          </Typography>
        </>
      )}

      {buildSection(t('terms_privacy_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('terms_privacy_governed')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('terms_privacy_website')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('terms_privacy_mobile')}
          </Typography>
          <Typography variant="body1">
            {t('terms_privacy_review')}
          </Typography>
        </>
      )}

      {buildSection(t('terms_termination_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('terms_termination_user')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('terms_termination_reserve')}
          </Typography>
          <Box sx={{ pl: 3, mb: 2 }}>
            <Typography variant="body1">• {t('terms_termination_violated')}</Typography>
            <Typography variant="body1">• {t('terms_termination_misuse')}</Typography>
            <Typography variant="body1">• {t('terms_termination_security')}</Typography>
          </Box>
        </>
      )}

      {buildSection(t('terms_changes_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('terms_changes_revise')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('terms_changes_updated')}
          </Typography>
          <Typography variant="body1">
            {t('terms_changes_acceptance')}
          </Typography>
        </>
      )}

      {buildSection(t('terms_law_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('terms_law_india')}
          </Typography>
          <Typography variant="body1">
            {t('terms_law_jurisdiction')}
          </Typography>
        </>
      )}

      {buildSection(t('terms_contact_title'),
        <>
          <Typography variant="body1" paragraph>
            {t('terms_contact_intro')}
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>{t('terms_contact_name')}</strong><br />
            {t('terms_contact_email')}
          </Typography>
          <Typography variant="body1">
            {t('terms_contact_response')}
          </Typography>
        </>
      )}

      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          bgcolor: '#FFEBEE',
          border: '2px solid #F44336',
          mt: 4
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="#C62828" gutterBottom>
          {t('terms_notice_heading')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('terms_notice_intro')}
        </Typography>
        <Box sx={{ pl: 3, mb: 2 }}>
          <Typography variant="body1">• {t('terms_notice_informational')}</Typography>
          <Typography variant="body1">• {t('terms_notice_not_affiliated')}</Typography>
          <Typography variant="body1">• {t('terms_notice_verify')}</Typography>
          <Typography variant="body1">• {t('terms_notice_responsibility')}</Typography>
        </Box>
        <Typography variant="body1" fontWeight="bold">
          {t('terms_notice_disagree')}
        </Typography>
      </Paper>
    </Container>
  </>
  );
};
